import { useState, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineInfo, MdOutlineWarningAmber } from "react-icons/md";
import { IoMdClose } from "react-icons/io";
import { SettingRow, NumberInput, Toggle, Section, SaveButton } from "./SettingsControls";
import Popup from "src/components/Popup/Popup";
import type { SendMethod } from "./useSettings";

type CsvFilter = "all" | "paid" | "paid_partial";
type CsvIntervalUnit = "days" | "weeks" | "months";

interface ChipInputProps {
    emails: string[];
    onChange: (emails: string[]) => void;
    placeholder?: string;
}

function CsvEmailChipInput({ emails, onChange, placeholder }: ChipInputProps) {
    const [draft, setDraft] = useState("");

    const commit = () => {
        const trimmed = draft.trim().toLowerCase();
        if (trimmed && !emails.includes(trimmed)) {
            onChange([...emails, trimmed]);
        }
        setDraft("");
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
        } else if (e.key === "Backspace" && draft === "" && emails.length > 0) {
            onChange(emails.slice(0, -1));
        }
    };

    const remove = (email: string) => onChange(emails.filter((x) => x !== email));

    return (
        <div className="csv-chip-input">
            {emails.map((email) => (
                <span key={email} className="csv-chip">
                    {email}
                    <button className="csv-chip__remove" type="button" onClick={() => remove(email)}>
                        <IoMdClose size={12} />
                    </button>
                </span>
            ))}
            <input
                className="csv-chip-input__field"
                type="email"
                placeholder={emails.length === 0 ? placeholder : ""}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={commit}
            />
        </div>
    );
}

const EMAIL_LINK_MODES = [
    { value: "login",   label: "Login",   descKey: "settings.emailLinkLoginDesc" },
    { value: "sms_otp", label: "SMS OTP", descKey: "settings.emailLinkSmsOtpDesc" },
] as const;

interface InvoiceSettingsProps {
    paidThreshold: number;
    sendMethods: SendMethod[];
    sendOnUpload: boolean;
    sendOnQuestionnaireSubmit: boolean;
    requireLegalAcceptance: boolean;
    questionnairePostOption: boolean;
    emailLinkModes: string[];
    questionsEnabled: boolean;
    tagsEnabled: boolean;
    hideCategoryLabels: boolean;
    saved: boolean;
    dirty?: boolean;
    onPaidThresholdChange: (v: number) => void;
    onToggleEmail: () => void;
    onEmailLinkModesChange: (modes: string[]) => void;
    onSendOnUploadChange: (v: boolean) => void;
    onSendOnQuestionnaireSubmitChange: (v: boolean) => void;
    onRequireLegalAcceptanceChange: (v: boolean) => void;
    onQuestionnairePostOptionChange: (v: boolean) => void;
    onQuestionsEnabledChange: (v: boolean) => void;
    onTagsEnabledChange: (v: boolean) => void;
    onHideCategoryLabelsChange: (v: boolean) => void;
    onSave: () => void;
    // CSV auto-report
    csvReportEnabled: boolean;
    csvReportEmails: string[];
    csvReportIntervalAmount: number;
    csvReportIntervalUnit: CsvIntervalUnit;
    csvReportFilter: CsvFilter;
    csvReportAmountMin: number;
    onCsvReportChange: (patch: Partial<{
        csvReportEnabled: boolean;
        csvReportEmails: string[];
        csvReportIntervalAmount: number;
        csvReportIntervalUnit: CsvIntervalUnit;
        csvReportFilter: CsvFilter;
        csvReportAmountMin: number;
    }>) => void;
}

export function InvoiceSettings(p: InvoiceSettingsProps) {
    const { t } = useTranslation();
    const emailEnabled = p.sendMethods.includes("email");
    const [legalWarningPending, setLegalWarningPending] = useState(false);

    const toggleMode = (mode: string) => {
        const next = p.emailLinkModes.includes(mode)
            ? p.emailLinkModes.filter((m) => m !== mode)
            : [...p.emailLinkModes, mode];
        // At least one must remain selected
        if (next.length === 0) return;
        p.onEmailLinkModesChange(next);
    };

    return (
        <Section title={t("settings.invoices")}>
            <SettingRow title={t("settings.paymentTolerance")} description={t("settings.paymentToleranceDesc")}>
                <div className="input-group">
                    <NumberInput value={p.paidThreshold} onChange={p.onPaidThresholdChange} min={0} max={100} />
                    <span className="settings-unit">€</span>
                </div>
            </SettingRow>

            <SettingRow title={t("settings.emailLinkEnable")} description={t("settings.emailLinkEnableDesc")}>
                <Toggle value={emailEnabled} onChange={p.onToggleEmail} />
            </SettingRow>

            <SettingRow title={t("settings.emailLinkOptions")} description={t("settings.emailLinkOptionsDesc")} disabled={!emailEnabled}>
                <div className="email-link-mode-list">
                    {EMAIL_LINK_MODES.map(({ value, label, descKey }) => (
                        <label key={value} className={`email-link-mode-item${!emailEnabled ? " email-link-mode-item--disabled" : ""}`}>
                            <div className="email-link-mode-item__text">
                                <span className="email-link-mode-item__label">{label}</span>
                                <span className="email-link-mode-item__desc">{t(descKey)}</span>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={p.emailLinkModes.includes(value)}
                                    disabled={!emailEnabled}
                                    onChange={() => toggleMode(value)}
                                />
                                <span className="toggle-switch__track" />
                            </label>
                        </label>
                    ))}
                </div>
            </SettingRow>

            <SettingRow title={t("settings.sendOnUpload")} description={t("settings.sendOnUploadDesc")} disabled={!emailEnabled}>
                <Toggle value={p.sendOnUpload} onChange={p.onSendOnUploadChange} disabled={!emailEnabled} />
            </SettingRow>
            {p.sendOnUpload && emailEnabled && (
                <div className="settings-info-banner settings-info-banner--neutral">
                    <MdOutlineInfo size={16} />
                    {t("settings.sendOnUploadHint")}
                </div>
            )}

            <SettingRow title={t("settings.sendOnQuestionnaireSubmit")} description={t("settings.sendOnQuestionnaireSubmitDesc")} disabled={!emailEnabled}>
                <Toggle value={p.sendOnQuestionnaireSubmit} onChange={p.onSendOnQuestionnaireSubmitChange} disabled={!emailEnabled} />
            </SettingRow>
            {p.sendOnQuestionnaireSubmit && emailEnabled && (
                <div className="settings-info-banner settings-info-banner--neutral">
                    <MdOutlineInfo size={16} />
                    {t("settings.sendOnQuestionnaireSubmitHint")}
                </div>
            )}

            <SettingRow title={t("settings.requireLegalAcceptance")} description={t("settings.requireLegalAcceptanceDesc")}>
                <Toggle
                    value={p.requireLegalAcceptance}
                    onChange={(v) => {
                        if (!v) {
                            setLegalWarningPending(true);
                        } else {
                            p.onRequireLegalAcceptanceChange(true);
                        }
                    }}
                />
            </SettingRow>
            {!p.requireLegalAcceptance && (
                <div className="settings-info-banner settings-info-banner--warn">
                    <MdOutlineWarningAmber size={16} />
                    {t("settings.requireLegalAcceptanceWarning")}
                </div>
            )}

            <SettingRow title={t("settings.questionnairePostOption")} description={t("settings.questionnairePostOptionDesc")}>
                <Toggle value={p.questionnairePostOption} onChange={p.onQuestionnairePostOptionChange} />
            </SettingRow>

            <SettingRow title={t("settings.questionsEnabled")} description={t("settings.questionsEnabledDesc")}>
                <Toggle value={p.questionsEnabled} onChange={p.onQuestionsEnabledChange} />
            </SettingRow>

            <SettingRow title={t("settings.tagsEnabled")} description={t("settings.tagsEnabledDesc")}>
                <Toggle value={p.tagsEnabled} onChange={p.onTagsEnabledChange} />
            </SettingRow>

            <SettingRow title={t("settings.hideCategoryLabels")} description={t("settings.hideCategoryLabelsDesc")}>
                <Toggle value={p.hideCategoryLabels} onChange={p.onHideCategoryLabelsChange} />
            </SettingRow>

            {legalWarningPending && (
                <Popup
                    title={t("settings.requireLegalAcceptance")}
                    message={t("settings.requireLegalAcceptanceConfirm")}
                    onClose={() => setLegalWarningPending(false)}
                    actions={[
                        {
                            label: t("popup.cancel"),
                            variant: "default",
                            onClick: () => setLegalWarningPending(false),
                        },
                        {
                            label: t("popup.confirm"),
                            variant: "danger",
                            onClick: () => {
                                p.onRequireLegalAcceptanceChange(false);
                                setLegalWarningPending(false);
                            },
                        },
                    ]}
                />
            )}

            <div className="settings-subsection-divider" />

            <SettingRow
                title={t("settings.csvAutoExport")}
                description={t("settings.csvAutoExportDesc")}
            >
                <Toggle value={p.csvReportEnabled} onChange={(v) => p.onCsvReportChange({ csvReportEnabled: v })} />
            </SettingRow>

            {p.csvReportEnabled && (
                <>
                    <SettingRow
                        title={t("settings.csvExtraEmails")}
                        description={t("settings.csvExtraEmailsDesc")}
                    >
                        <CsvEmailChipInput
                            emails={p.csvReportEmails}
                            onChange={(emails) => p.onCsvReportChange({ csvReportEmails: emails })}
                            placeholder={t("settings.csvExtraEmailsPlaceholder")}
                        />
                    </SettingRow>

                    <SettingRow title={t("settings.csvInterval")} description={t("settings.csvIntervalDesc")}>
                        <div className="input-group">
                            <NumberInput
                                value={p.csvReportIntervalAmount}
                                onChange={(v) => p.onCsvReportChange({ csvReportIntervalAmount: v })}
                                min={1}
                                max={99}
                            />
                            <select
                                className="settings-select"
                                value={p.csvReportIntervalUnit}
                                onChange={(e) => p.onCsvReportChange({ csvReportIntervalUnit: e.target.value as CsvIntervalUnit })}
                            >
                                <option value="days">{t("settings.csvIntervalDays")}</option>
                                <option value="weeks">{t("settings.csvIntervalWeeks")}</option>
                                <option value="months">{t("settings.csvIntervalMonths")}</option>
                            </select>
                        </div>
                    </SettingRow>

                    <SettingRow title={t("settings.csvWhichInvoices")} description={t("settings.csvWhichInvoicesDesc")}>
                        <select
                            className="settings-select"
                            value={p.csvReportFilter}
                            onChange={(e) => p.onCsvReportChange({ csvReportFilter: e.target.value as CsvFilter })}
                        >
                            <option value="all">{t("settings.csvFilterAll")}</option>
                            <option value="paid">{t("settings.csvFilterPaid")}</option>
                            <option value="paid_partial">{t("settings.csvFilterPaidPartial")}</option>
                        </select>
                    </SettingRow>

                    <SettingRow title={t("settings.csvAmountMin")} description={t("settings.csvAmountMinDesc")}>
                        <div className="input-group">
                            <NumberInput
                                value={p.csvReportAmountMin}
                                onChange={(v) => p.onCsvReportChange({ csvReportAmountMin: Math.max(0, v) })}
                                min={0}
                                max={99999}
                            />
                            <span className="settings-unit">€</span>
                        </div>
                    </SettingRow>
                </>
            )}
        </Section>
    );
}
